import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiGet } from "../api/client";

// Lightweight typings — the real shape lives on the API; we only require what
// each home renders. Unknown extras pass through harmlessly.

export interface AgentCoverageAlert {
  id: string;
  agent: string;
  kind: "expiring" | "degraded" | "gap";
  text: string;
  action: string;
}

export interface AgentCoverageMap {
  alerts: AgentCoverageAlert[];
  summary?: { healthy: number; degraded: number; expiring: number };
}

export interface PlaySummary {
  id: string;
  title: string;
  state: string;
  health?: "red" | "amber" | "green";
  issue?: string;
}

export interface StepSummary {
  id: string;
  type: "task" | "decision" | "comm" | "query" | "milestone" | "wait" | "micro_task" | "instrumentation";
  title: string;
  playId: string;
  playTitle: string;
  workspaceId: string;
  state: string;
  ord: number;
  dueAt?: string;
  priority?: "low" | "normal" | "high";
}

interface MeStepsEnvelope {
  items: Array<{
    step: {
      id: string;
      playId: string;
      ord: number;
      type: StepSummary["type"];
      title: string;
      state: string;
    };
    play: {
      id: string;
      archetype: string;
      workspaceId: string;
      workspaceName: string;
      targetWindowEnd: string | null;
    };
  }>;
}

function envelopeToSummaries(env: MeStepsEnvelope): StepSummary[] {
  return env.items.map((it) => {
    const base: StepSummary = {
      id: it.step.id,
      type: it.step.type,
      title: it.step.title,
      playId: it.play.id,
      playTitle: it.play.archetype,
      workspaceId: it.play.workspaceId,
      state: it.step.state,
      ord: it.step.ord,
    };
    if (it.play.targetWindowEnd !== null) {
      base.dueAt = it.play.targetWindowEnd;
    }
    return base;
  });
}

/**
 * Endpoints called here may land in later chunks; we swallow errors and fall
 * back to an empty/quiet shape so the persona homes can still render their
 * empty states without a screen-blocking error.
 */
async function fetchOrEmpty<T>(path: string, fallback: T): Promise<T> {
  try {
    return await apiGet<T>(path);
  } catch {
    return fallback;
  }
}

export function useAgentAlerts(workspaceId: string | null | undefined): UseQueryResult<AgentCoverageMap, Error> {
  return useQuery<AgentCoverageMap, Error>({
    queryKey: ["agentAlerts", workspaceId],
    queryFn: () =>
      fetchOrEmpty<AgentCoverageMap>(
        `/api/workspaces/${workspaceId}/agents/coverage-map`,
        { alerts: [] },
      ),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

export function usePlaysAtRisk(workspaceId: string | null | undefined): UseQueryResult<PlaySummary[], Error> {
  return useQuery<PlaySummary[], Error>({
    queryKey: ["playsAtRisk", workspaceId],
    queryFn: () =>
      fetchOrEmpty<PlaySummary[]>(`/api/workspaces/${workspaceId}/plays?state=running`, []),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

export function useMySteps(state?: string): UseQueryResult<StepSummary[], Error> {
  const path = state ? `/api/me/steps?state=${encodeURIComponent(state)}` : "/api/me/steps";
  return useQuery<StepSummary[], Error>({
    queryKey: ["me-steps", "summary", state ?? "default"],
    queryFn: async () => {
      const env = await fetchOrEmpty<MeStepsEnvelope>(path, { items: [] });
      return envelopeToSummaries(env);
    },
    staleTime: 30_000,
  });
}
